if (!customElements.get('chatbot-launcher')) {
  const STORAGE_KEY = 'chatbot-launcher-state';

  customElements.define(
    'chatbot-launcher',
    class ChatbotLauncher extends HTMLElement {
      constructor() {
        super();
        this.openZendeskInterval = null;
        this.activeBot = null;
        this.adaStarted = false;
        this.adaStartPromise = null;
        this.adaEventsBound = false;
        this.zendeskEventsBound = false;
        this.launching = false;
        this.launchingButton = null;
        this.sessions = { ada: false, zendesk: false };
        this.unread = { ada: 0, zendesk: 0 };
        this.seenAdaMessageIds = new Set();
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onBotClosed = this.onBotClosed.bind(this);
        this.onAdaToggle = this.onAdaToggle.bind(this);
      }

      connectedCallback() {
        this.trigger = this.querySelector('[data-launcher-trigger]');
        this.panel = this.querySelector('[data-launcher-panel]');
        this.closeButton = this.querySelector('[data-launcher-close]');
        this.unreadBadge = this.querySelector('[data-unread-badge]');
        this.unreadStatus = this.querySelector('[data-unread-status]');
        this.errorEl = this.querySelector('[data-launcher-error]');
        this.options = this.querySelectorAll('[data-launcher-option]');
        this.continueOptions = {
          ada: this.querySelector('[data-continue-option][data-chatbot="ada"]'),
          zendesk: this.querySelector('[data-continue-option][data-chatbot="zendesk"]'),
        };

        if (!this.trigger || !this.panel) return;

        this.adaHandle = this.dataset.adaHandle || '';
        this.zendeskKey = this.dataset.zendeskKey || '';
        this.bubbleLabel = this.dataset.bubbleLabel || this.trigger.getAttribute('aria-label') || 'Open chat';
        this.errorMessage = this.dataset.errorMessage || "Couldn't start chat. Try again.";
        this.lastAnnouncedUnread = null;

        this.restoreState();

        this.trigger.addEventListener('click', () => this.toggle());
        this.closeButton?.addEventListener('click', () => this.close());

        this.options.forEach((option) => {
          option.addEventListener('click', (event) =>
            this.handleOptionClick(option.dataset.chatbot, false, event.currentTarget)
          );
        });

        Object.values(this.continueOptions).forEach((option) => {
          option?.addEventListener('click', (event) =>
            this.handleOptionClick(option.dataset.chatbot, true, event.currentTarget)
          );
        });

        this.updateContinueOptions();
        this.updateUnreadBadge();
        this.rehydrateSessions();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        clearInterval(this.openZendeskInterval);
        removeTrapFocus();
      }

      restoreState() {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY);
          if (!raw) return;

          const saved = JSON.parse(raw);
          this.sessions = {
            ada: Boolean(saved?.sessions?.ada),
            zendesk: Boolean(saved?.sessions?.zendesk),
          };
          this.unread = {
            ada: Math.max(0, Number(saved?.unread?.ada) || 0),
            zendesk: Math.max(0, Number(saved?.unread?.zendesk) || 0),
          };
        } catch (error) {
          // Ignore invalid storage
        }
      }

      persistState() {
        try {
          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              sessions: this.sessions,
              unread: this.unread,
            }),
          );
        } catch (error) {
          // Private mode / quota — ignore
        }
      }

      rehydrateSessions() {
        if (this.sessions.ada && this.adaHandle) {
          this.ensureAdaReady({ openDrawer: false }).catch((error) => {
            console.error(error);
            this.markSession('ada', false);
          });
        }
        if (this.sessions.zendesk && this.zendeskKey) {
          this.ensureZendeskReady({ openWidget: false }).catch((error) => {
            console.error(error);
            this.markSession('zendesk', false);
          });
        }
      }

      get isOpen() {
        return this.panel.classList.contains('opacity-100');
      }

      get totalUnread() {
        return (this.unread.ada || 0) + (this.unread.zendesk || 0);
      }

      handleOptionClick(chatbot, isContinue = false, button = null) {
        if (this.launching) return;
        this.launching = true;
        this.clearLaunchError();
        this.setOptionLoading(button, true);

        if (chatbot === 'zendesk') {
          this.launchZendesk(isContinue);
        } else {
          this.launchAda(isContinue);
        }
      }

      setOptionLoading(button, isLoading) {
        if (!button) return;

        this.launchingButton = isLoading ? button : null;
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        const label = button.querySelector('[data-option-label], [data-continue-label]');
        const count = button.querySelector('[data-continue-count]');
        const loading = button.querySelector('[data-option-loading]');

        label?.classList.toggle('invisible', isLoading);
        count?.classList.toggle('invisible', isLoading);
        loading?.classList.toggle('hidden', !isLoading);
      }

      clearOptionLoading() {
        if (this.launchingButton) {
          this.setOptionLoading(this.launchingButton, false);
        }
        this.launchingButton = null;
        this.launching = false;
      }

      showLaunchError(message = this.errorMessage) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('hidden');
      }

      clearLaunchError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }

      handleLaunchFailure(bot, error) {
        if (error) console.error(error);
        this.activeBot = null;
        this.markSession(bot, false);
        this.classList.remove('hidden');
        this.hideVendorLaunchers();
        this.updateContinueOptions();
        this.updateUnreadBadge();
        this.clearOptionLoading();
        if (!this.isOpen) this.open();
        this.showLaunchError();
      }

      withTimeout(promise, timeoutMs, message) {
        return new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
          promise.then(
            (value) => {
              clearTimeout(timer);
              resolve(value);
            },
            (error) => {
              clearTimeout(timer);
              reject(error);
            }
          );
        });
      }

      waitFor(predicate, timeoutMs = 8000, intervalMs = 100) {
        return new Promise((resolve, reject) => {
          const startedAt = Date.now();

          const tick = async () => {
            let ready = false;
            try {
              ready = await predicate();
            } catch (error) {
              ready = false;
            }

            if (ready) {
              resolve(true);
              return;
            }

            if (Date.now() - startedAt > timeoutMs) {
              reject(new Error('Timed out waiting for chatbot'));
              return;
            }

            setTimeout(tick, intervalMs);
          };

          tick();
        });
      }

      toggle() {
        this.isOpen ? this.close() : this.open();
      }

      open() {
        this.updateContinueOptions();
        this.panel.classList.remove('opacity-0', 'pointer-events-none', 'translate-y-4', 'scale-95');
        this.panel.classList.add('opacity-100', 'pointer-events-auto', 'translate-y-0', 'scale-100');
        this.panel.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');

        const focusTarget =
          this.panel.querySelector('[data-continue-option]:not(.hidden)') ||
          this.panel.querySelector('[data-launcher-option]') ||
          this.closeButton ||
          this.panel;

        trapFocus(this.panel, focusTarget);

        document.addEventListener('keydown', this.onKeydown);
        requestAnimationFrame(() => {
          document.addEventListener('click', this.onDocumentClick);
        });
      }

      close() {
        this.panel.classList.remove('opacity-100', 'pointer-events-auto', 'translate-y-0', 'scale-100');
        this.panel.classList.add('opacity-0', 'pointer-events-none', 'translate-y-4', 'scale-95');
        this.panel.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.clearLaunchError();

        removeTrapFocus(this.trigger);

        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
      }

      hideLauncher() {
        this.close();
        this.classList.add('hidden');
      }

      showLauncher() {
        this.classList.remove('hidden');
        this.hideVendorLaunchers();
        this.updateContinueOptions();
        this.updateUnreadBadge();
      }

      hideVendorLaunchers() {
        document.getElementById('ada-button-frame')?.classList.add('!hidden');

        try {
          window.zE?.('webWidget', 'hide');
        } catch (error) {
          // Classic widget may not be present
        }
        try {
          window.zE?.('messenger', 'hide');
        } catch (error) {
          // Messaging widget may not be present
        }
      }

      clearUnread(bot) {
        if (!bot) {
          this.unread.ada = 0;
          this.unread.zendesk = 0;
        } else {
          this.unread[bot] = 0;
        }
        this.persistState();
        this.updateUnreadBadge();
        this.updateContinueOptions();
      }

      setUnread(bot, count) {
        this.unread[bot] = Math.max(0, Number(count) || 0);
        this.persistState();
        this.updateUnreadBadge();
        this.updateContinueOptions();
      }

      bumpUnread(bot) {
        this.unread[bot] = (this.unread[bot] || 0) + 1;
        this.persistState();
        this.updateUnreadBadge();
        this.updateContinueOptions();
      }

      updateUnreadBadge() {
        const total = this.totalUnread;
        const unreadText =
          total === 1 ? '1 unread message' : total > 1 ? `${total > 9 ? '9+' : total} unread messages` : '';

        if (this.unreadBadge) {
          if (total > 0) {
            this.unreadBadge.textContent = total > 9 ? '9+' : String(total);
            this.unreadBadge.classList.remove('hidden');
          } else {
            this.unreadBadge.textContent = '';
            this.unreadBadge.classList.add('hidden');
          }
          this.unreadBadge.setAttribute('aria-hidden', 'true');
        }

        if (this.trigger) {
          this.trigger.setAttribute(
            'aria-label',
            unreadText ? `${this.bubbleLabel}, ${unreadText}` : this.bubbleLabel
          );
        }

        if (this.unreadStatus && total !== this.lastAnnouncedUnread) {
          this.unreadStatus.textContent = unreadText;
          this.lastAnnouncedUnread = total;
        }
      }

      updateContinueOptions() {
        Object.entries(this.continueOptions).forEach(([bot, button]) => {
          if (!button) return;
          const show = this.sessions[bot];
          button.classList.toggle('hidden', !show);
          button.disabled = !show;

          const countEl = button.querySelector('[data-continue-count]');
          if (!countEl) return;

          const count = this.unread[bot] || 0;
          if (show && count > 0) {
            countEl.textContent = count > 9 ? '9+' : String(count);
            countEl.classList.remove('hidden');
            countEl.setAttribute('aria-hidden', 'false');
          } else {
            countEl.textContent = '';
            countEl.classList.add('hidden');
            countEl.setAttribute('aria-hidden', 'true');
          }
        });
      }

      markSession(bot, active) {
        this.sessions[bot] = Boolean(active);
        if (!active) this.clearUnread(bot);
        else this.persistState();
        this.updateContinueOptions();
      }

      onAdaToggle(isDrawerOpen) {
        if (isDrawerOpen) {
          this.clearUnread('ada');
          return;
        }
        this.onBotClosed();
      }

      onBotClosed() {
        if (!this.activeBot) return;

        const bot = this.activeBot;
        this.activeBot = null;

        // Keep the session resumable; only hide vendor chrome
        if (bot === 'ada') {
          this.markSession('ada', true);
          document.getElementById('ada-button-frame')?.classList.add('!hidden');
        }

        if (bot === 'zendesk') {
          this.markSession('zendesk', true);
          try {
            window.zE?.('webWidget', 'hide');
          } catch (error) {
            // ignore
          }
          try {
            window.zE?.('messenger', 'hide');
          } catch (error) {
            // ignore
          }
        }

        this.showLauncher();
      }

      endSession(bot) {
        this.markSession(bot, false);
        if (this.activeBot === bot) this.activeBot = null;
        this.showLauncher();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      loadScript({ id, src, attributes = {} }) {
        return new Promise((resolve, reject) => {
          const existing = document.getElementById(id);
          if (existing) {
            resolve(existing);
            return;
          }

          const script = document.createElement('script');
          script.id = id;
          script.src = src;
          script.async = true;

          Object.entries(attributes).forEach(([key, value]) => {
            script.setAttribute(key, value);
          });

          script.onload = () => resolve(script);
          script.onerror = () => reject(new Error(`Failed to load ${src}`));
          document.head.appendChild(script);
        });
      }

      async bindAdaEvents() {
        if (this.adaEventsBound || !window.adaEmbed?.subscribeEvent) return;

        try {
          await window.adaEmbed.subscribeEvent('ada:conversation:message', (data) => {
            const messageId = data?.message_id;
            if (messageId) {
              if (this.seenAdaMessageIds.has(messageId)) return;
              this.seenAdaMessageIds.add(messageId);
            }

            if (this.activeBot !== 'ada') this.bumpUnread('ada');
            this.markSession('ada', true);
          });

          await window.adaEmbed.subscribeEvent('ada:end_conversation', () => {
            this.seenAdaMessageIds.clear();
            this.endSession('ada');
          });

          this.adaEventsBound = true;
        } catch (error) {
          console.error(error);
        }
      }

      waitForAdaEmbed(timeoutMs = 10000) {
        if (window.adaEmbed) return Promise.resolve();

        return new Promise((resolve, reject) => {
          const startedAt = Date.now();
          const interval = setInterval(() => {
            if (window.adaEmbed) {
              clearInterval(interval);
              resolve();
              return;
            }
            if (Date.now() - startedAt > timeoutMs) {
              clearInterval(interval);
              reject(new Error('Ada embed failed to load'));
            }
          }, 50);
        });
      }

      async ensureAdaStarted() {
        if (!this.adaHandle) return;
        if (this.adaStarted) return;
        if (this.adaStartPromise) return this.adaStartPromise;

        this.adaStartPromise = (async () => {
          await this.loadScript({
            id: '__ada',
            src: 'https://static.ada.support/embed2.js',
            attributes: {
              'data-handle': this.adaHandle,
              'data-lazy': '',
            },
          });

          await this.waitForAdaEmbed();

          const started = window.adaEmbed.start({
            handle: this.adaHandle,
            toggleCallback: this.onAdaToggle,
          });
          if (started && typeof started.then === 'function') {
            await started;
          }

          this.adaStarted = true;
          await this.bindAdaEvents();
        })().catch((error) => {
          this.adaStartPromise = null;
          throw error;
        });

        return this.adaStartPromise;
      }

      async ensureAdaReady({ openDrawer = false } = {}) {
        if (!this.adaHandle) throw new Error('Ada handle is missing');

        await this.ensureAdaStarted();

        if (openDrawer) {
          document.getElementById('ada-button-frame')?.classList.remove('!hidden');
          const isOpen =
            typeof window.adaEmbed.isOpen === 'function' ? await window.adaEmbed.isOpen() : false;
          if (!isOpen) await window.adaEmbed.toggle();

          await this.waitFor(async () => {
            try {
              return typeof window.adaEmbed.isOpen === 'function'
                ? await window.adaEmbed.isOpen()
                : Boolean(document.getElementById('ada-button-frame'));
            } catch (error) {
              return false;
            }
          }, 8000);
        } else {
          document.getElementById('ada-button-frame')?.classList.add('!hidden');
          const isOpen =
            typeof window.adaEmbed.isOpen === 'function' ? await window.adaEmbed.isOpen() : false;
          if (isOpen) await window.adaEmbed.toggle();
        }
      }

      async launchAda() {
        if (!this.adaHandle) {
          this.handleLaunchFailure('ada');
          return;
        }

        this.activeBot = 'ada';
        this.clearUnread('ada');

        try {
          await this.withTimeout(
            this.ensureAdaReady({ openDrawer: true }),
            12000,
            'Ada launch timed out'
          );
          this.markSession('ada', true);
          this.hideLauncher();
          this.clearOptionLoading();
        } catch (error) {
          this.handleLaunchFailure('ada', error);
        }
      }

      bindZendeskEvents() {
        if (this.zendeskEventsBound || typeof window.zE !== 'function') return;

        try {
          window.zE('webWidget:on', 'close', this.onBotClosed);
        } catch (error) {
          // Classic API unavailable
        }

        try {
          window.zE('messenger:on', 'close', this.onBotClosed);
        } catch (error) {
          // Messaging API unavailable
        }

        try {
          window.zE('webWidget:on', 'chat:unreadMessages', (count) => {
            this.setUnread('zendesk', count);
            if (count > 0) this.markSession('zendesk', true);
          });
        } catch (error) {
          // Classic unread API unavailable
        }

        try {
          window.zE('messenger:on', 'unreadMessages', (count) => {
            this.setUnread('zendesk', count);
            if (count > 0) this.markSession('zendesk', true);
          });
        } catch (error) {
          // Messaging unread API unavailable
        }

        try {
          window.zE('webWidget:on', 'chat:end', () => {
            this.endSession('zendesk');
          });
        } catch (error) {
          // Classic end API unavailable
        }

        try {
          window.zE('webWidget:on', 'chat:start', () => {
            this.markSession('zendesk', true);
          });
        } catch (error) {
          // ignore
        }

        this.zendeskEventsBound = true;
      }

      isZendeskVisible() {
        try {
          const display = window.zE?.('webWidget:get', 'display');
          if (display && display !== 'hidden') return true;
        } catch (error) {
          // Classic get API unavailable
        }

        return Boolean(
          document.querySelector(
            '#launcher, iframe#webWidget, iframe[data-product="web_widget"], iframe[title*="Messaging" i], iframe[name*="Messaging" i]'
          )
        );
      }

      async ensureZendeskReady({ openWidget = false } = {}) {
        if (!this.zendeskKey) throw new Error('Zendesk key is missing');

        const lang = document.documentElement.lang || 'en';

        window.zESettings = {
          webWidget: {
            chat: {
              concierge: {
                name: 'Hush Live Support',
              },
            },
          },
        };

        if (typeof window.zE !== 'function') {
          await this.loadScript({
            id: 'ze-snippet',
            src: `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(this.zendeskKey)}`,
          });

          await this.waitFor(() => typeof window.zE === 'function', 10000);
        }

        this.bindZendeskEvents();

        await new Promise((resolve, reject) => {
          let settled = false;
          const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error('Zendesk failed to initialize'));
          }, 10000);

          try {
            window.zE(() => {
              if (settled) return;

              try {
                if (typeof window.zE.setLocale === 'function') {
                  window.zE.setLocale(lang);
                }

                if (openWidget) {
                  try {
                    window.zE('webWidget', 'show');
                  } catch (error) {
                    // ignore
                  }
                  try {
                    window.zE('messenger', 'show');
                  } catch (error) {
                    // ignore
                  }
                  if (typeof window.zE.activate === 'function') {
                    window.zE.activate();
                  } else {
                    try {
                      window.zE('messenger', 'open');
                    } catch (error) {
                      window.zE('webWidget', 'open');
                    }
                  }
                } else {
                  try {
                    window.zE('webWidget', 'hide');
                  } catch (error) {
                    // ignore
                  }
                  try {
                    window.zE('messenger', 'hide');
                  } catch (error) {
                    // ignore
                  }
                }

                settled = true;
                clearTimeout(timer);
                resolve();
              } catch (error) {
                settled = true;
                clearTimeout(timer);
                reject(error);
              }
            });
          } catch (error) {
            settled = true;
            clearTimeout(timer);
            reject(error);
          }
        });

        if (openWidget) {
          await this.waitFor(() => this.isZendeskVisible(), 8000);
        }
      }

      async launchZendesk() {
        if (!this.zendeskKey) {
          this.handleLaunchFailure('zendesk');
          return;
        }

        this.activeBot = 'zendesk';
        this.clearUnread('zendesk');

        try {
          await this.withTimeout(
            this.ensureZendeskReady({ openWidget: true }),
            15000,
            'Zendesk launch timed out'
          );
          this.markSession('zendesk', true);
          this.hideLauncher();
          this.clearOptionLoading();
        } catch (error) {
          this.handleLaunchFailure('zendesk', error);
        }
      }
    },
  );
}
